import { useState } from 'react';
import { Button, Dialog, Input } from '@orion/ui';
import { ApiError } from '../../../lib/api-client.js';
import { useConnectImap } from './api.js';

/** The "run unattended" fallback (docs section "Inbox Pulse" § Phase 1) —
 * no OAuth, no 7-day Testing-mode token expiry, just a host/username/app
 * password IMAP sits behind. Requires 2-Step Verification on the account
 * to generate an app password in the first place; the page text below says
 * so rather than letting a plain account password fail silently against
 * most providers. */
export function ConnectImapDialog({ onClose, initialEmail }: { onClose: () => void; initialEmail?: string }) {
  const connect = useConnectImap();
  const [emailAddress, setEmailAddress] = useState(initialEmail ?? '');
  const [imapHost, setImapHost] = useState('imap.gmail.com');
  const [imapPort, setImapPort] = useState('993');
  const [imapUsername, setImapUsername] = useState('');
  const [imapAppPassword, setImapAppPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await connect.mutateAsync({
        emailAddress,
        imapHost,
        imapPort: Number(imapPort) || 993,
        imapUsername: imapUsername || emailAddress,
        imapAppPassword,
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.title : 'Could not connect that mailbox.');
    }
  };

  return (
    <Dialog
      open
      title="Connect via IMAP + App Password"
      onClose={onClose}
      actions={
        <Button
          size="sm"
          loading={connect.isPending}
          disabled={!emailAddress || !imapHost || !imapAppPassword}
          onClick={submit}
        >
          Connect
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs leading-4 text-text-muted">
          Requires 2-Step Verification on the account, with an App Password generated for it — not the account's regular sign-in password.
        </p>
        {error && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}
        <Input label="Email address" type="email" value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} placeholder="you@gmail.com" />
        <div className="grid grid-cols-[1fr_100px] gap-3">
          <Input label="IMAP host" value={imapHost} onChange={(e) => setImapHost(e.target.value)} />
          <Input label="Port" type="number" value={imapPort} onChange={(e) => setImapPort(e.target.value)} />
        </div>
        <Input
          label="Username"
          helpText="Leave blank to use the email address above."
          value={imapUsername}
          onChange={(e) => setImapUsername(e.target.value)}
        />
        <Input label="App password" type="password" value={imapAppPassword} onChange={(e) => setImapAppPassword(e.target.value)} />
      </div>
    </Dialog>
  );
}
